import {
  browserSupportsWebAuthn,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
  startAuthentication,
  startRegistration,
  WebAuthnError,
} from "@simplewebauthn/browser";
import type {
  AuthSession,
  PasskeySummary,
  WebauthnOptions,
  WebauthnResponse,
} from "@websede/contracts";
import { api } from "../api/client.js";

export const passkeysSupported = (): boolean =>
  typeof window !== "undefined" && browserSupportsWebAuthn();

/** True when the user closed or timed out the browser prompt rather than hitting a real error. */
export function isCeremonyCancelled(error: unknown): boolean {
  if (error instanceof WebAuthnError) {
    return error.name === "NotAllowedError" || error.name === "AbortError";
  }
  return error instanceof DOMException && error.name === "NotAllowedError";
}

/** Server options → browser ceremony → server verification, for adding a passkey. */
export async function registerPasskey(name: string): Promise<PasskeySummary> {
  const options = await api.auth.passkeys.registerOptions();
  const response = await startRegistration({
    optionsJSON: asCreationOptions(options),
  });
  return api.auth.passkeys.registerVerify(name, asContractResponse(response));
}

/** Server options → browser ceremony → server verification, for signing in with a passkey. */
export async function signInWithPasskey(): Promise<AuthSession> {
  const options = await api.auth.passkeys.loginOptions();
  const response = await startAuthentication({
    optionsJSON: asRequestOptions(options),
  });
  return api.auth.passkeys.loginVerify(asContractResponse(response));
}

/** The browser's credential JSON carries the same fields the contract pins; index signatures differ. */
function asContractResponse(response: { id: string; rawId: string }): WebauthnResponse {
  return response as unknown as WebauthnResponse;
}

function asCreationOptions(options: WebauthnOptions): PublicKeyCredentialCreationOptionsJSON {
  return options as unknown as PublicKeyCredentialCreationOptionsJSON;
}

function asRequestOptions(options: WebauthnOptions): PublicKeyCredentialRequestOptionsJSON {
  return options as unknown as PublicKeyCredentialRequestOptionsJSON;
}
